import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../../../hooks/useStore';
import { DataViewEditBox, FormTextField } from '../../../../lib/components';
import { GroupsDataForm } from './form';
import { EmployeeListItem, GroupRequest } from '@pxtra-client-lib-legacy/gen';
import { CompanyTable } from '../shared/companyTable';
import { GroupCountCell } from '../../../partials/table/groupCountCell';
import { GridActionsCellItem, GridEnrichedColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { DeleteIcon, EditIcon } from '../../../../lib/icons';
import { useDialog } from '../../../../lib/components/compound/dialogs/confirmationDialog/useDialog';
import { ConfirmDeleteDialog } from '../shared/confirmDelete';
import { useGroupContext } from './context';
import { GroupsDataView } from './view';
import { FormDialog } from '../../../../lib/components/compound/dialogs/formDialog';
import { ContentHead } from '../../../../lib/components';
import { ActionArea } from '../../../../lib/components/compound/pages/head/actionArea';
import EmptyTable from '../../../../lib/icons/svg/empty-table.svg';
import { Paper } from '@mui/material';

const GroupCountCellRenderer: FC<{ rowName: string; mode: string }> = ({ rowName, mode }) => {
  const { employeeStore } = useStore();
  const [groupCount, setGroupCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (mode === 'edit') return;
    const fetchCount = async () => {
      const employees = await employeeStore.fetchList(['member_groups']);
      const count = employees?.filter(
        (employee: EmployeeListItem) =>
          Array.isArray(employee.member_groups) &&
          employee.member_groups.some((group) => group.name === rowName)
      )?.length;
      setGroupCount(count);
    };
    fetchCount();
  }, [mode, rowName, employeeStore]);

  return <GroupCountCell count={groupCount} />;
};

export const GroupData = observer(() => {
  const { t } = useTranslation();
  const { companyStore, employeeStore } = useStore();
  const params = useParams();
  const queryClient = useQueryClient();
  const dialog = useDialog<string>();
  const addDialog = useDialog<GroupRequest>();
  const updateDialog = useDialog<string>();

  const {
    selectedGroup,
    setSelectedGroup,
    mode,
    setMode,
    canSubmit,
    getAddedIds,
    getRemovedIds,
    clearChanges
  } = useGroupContext();

  useEffect(() => {
    const abortController = new AbortController();

    const fetchGroups = async () => {
      if (companyStore.groups && companyStore.groups?.length > 0) return;
      try {
        await companyStore.fetchGroups();
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };
    fetchGroups();

    return () => abortController.abort();
  }, [companyStore.company?.id]);

  const groups =
    companyStore.groups?.map((group) => ({
      id: group.id,
      name: group.name
    })) || [];

  const groupName = companyStore.groups?.find((g) => g.id === selectedGroup)?.name || '';

  const openAddDialog = () => {
    addDialog.setDialogRelevantValue('values' as unknown as GroupRequest);
  };

  const handleCreateGroup = async (values: Record<string, unknown>) => {
    try {
      await companyStore.createGroup(values as GroupRequest);
      const newGroup = companyStore.groups?.at(-1);
      if (newGroup?.id) {
        setSelectedGroup(newGroup.id);
      }
      addDialog.dialogProps.onClose?.();
    } catch (error) {
      console.error(error);
    }
  };

  const columns: Array<GridEnrichedColDef> = [
    {
      field: 'name',
      headerName: t('company:GROUP'),
      flex: 1,
      minWidth: 155
    },
    {
      field: 'count',
      headerName: '',
      flex: 0,
      align: 'right',
      renderCell: (cellParams) => (
        <GroupCountCellRenderer rowName={cellParams.row.name} mode={mode} />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`${params.id}`}
          icon={<EditIcon />}
          showInMenu
          label={'Umbenennen'}
          onClick={() => updateDialog.setDialogRelevantValue(params.id as string)}
          placeholder=""
        />,
        <GridActionsCellItem
          key={`${params.id}-delete`}
          icon={<DeleteIcon />}
          showInMenu
          label={t('common:DELETE')}
          onClick={() => dialog.setDialogRelevantValue(params.id as string)}
          placeholder=""
        />
      ]
    }
  ];

  if (groups.length === 0) {
    return (
      <Paper className="h-full">
        <ContentHead
          headline={t('company:GROUPS')}
          showActions={true}
          renderActions={() => (
            <ActionArea
              buttonConfig={{
                color: 'secondary',
                onClick: openAddDialog,
                text: t('company:GROUP_FORM_ADD_TITLE')
              }}
            />
          )}
        />
        <div className="flex flex-col items-center justify-center py-16">
          <img src={EmptyTable} alt={t('common:NO_DATA')} className="w-64 h-64" />
          <div className="font-medium text-base">{t('common:EMPTY_TABLE_TITLE_rows')}</div>
        </div>
        {addDialog.dialogProps.open && (
          <FormDialog
            open
            title={t('company:GROUP_FORM_ADD_TITLE')}
            width="360px"
            onClose={addDialog.dialogProps.onClose}
            onSubmit={handleCreateGroup}
          >
            <FormTextField placeholder={t('tableTitle:NAME')} name="name" autoFocus />
          </FormDialog>
        )}
      </Paper>
    );
  }

  return (
    <>
      <DataViewEditBox
        noPadding
        title={mode === 'edit' ? groupName : t('company:GROUPS')}
        view={() => <GroupsDataView />}
        sidebarComponent={
          <CompanyTable
            sx={{
              padding: 3,
              width: mode === 'view' ? '310px' : '0',
              opacity: mode === 'view' ? 1 : 0,
              transition: 'opacity',
              transitionDuration: '.5s',
              userSelect: 'none',
              '.MuiDataGrid-row:first-of-type': {
                borderColor: 'transparent'
              },
              '& .Mui-selected': {
                '& .MuiDataGrid-cell:first-of-tpye': {
                  fontWeight: 'medium'
                }
              },
              '.MuiDataGrid-cell:hover': {
                cursor: 'pointer'
              },
              '& .MuiDataGrid-row .MuiDataGrid-cell': {
                marginRight: '-32px'
              }
            }}
            rows={groups}
            columns={columns}
            onRowDoubleClick={() => {
              setMode('edit');
            }}
            hideToolbar
            hideHeader
            disableSelectionOnClick={false}
            onSelectionModelChange={(model) => {
              setSelectedGroup(model.at(0)?.toString());
            }}
            disableMultipleSelection
            selectionModel={selectedGroup}
          />
        }
        sidebarComponentWidth={mode === 'view' ? '310px' : '0'}
        edit={() => <GroupsDataForm />}
        canEdit
        initMode={mode}
        canSubmit={canSubmit}
        onSubmit={async () => {
          setMode('view');
          try {
            await employeeStore.bulkUpdateEmployeeGroupData({
              employees: {
                add: getAddedIds(),
                remove: getRemovedIds()
              },
              group: params.groupId
            });
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ['employees'] }),
              queryClient.invalidateQueries({ queryKey: ['employee'] })
            ]);
          } catch (error) {
            console.error(error);
          }
          clearChanges();
        }}
        onModeChanged={() => {
          clearChanges();
        }}
        onCancel={() => {
          setMode('view');
        }}
        actionAreaProps={{
          buttonConfig:
            mode === 'view'
              ? {
                  color: 'secondary',
                  onClick: openAddDialog,
                  text: t('company:GROUP_FORM_ADD_TITLE')
                }
              : undefined,
          threePointMenuConfig: []
        }}
      />
      {addDialog.dialogProps.open && (
        <FormDialog
          open
          title={t('company:GROUP_FORM_ADD_TITLE')}
          width="360px"
          onClose={addDialog.dialogProps.onClose}
          onSubmit={handleCreateGroup}
        >
          <FormTextField placeholder={t('tableTitle:NAME')} name="name" autoFocus />
        </FormDialog>
      )}

      <FormDialog
        initialValues={{
          name:
            companyStore.groups?.find((g) => g.id === updateDialog.dialogRelevantValue)?.name || ''
        }}
        open={updateDialog.dialogProps.open}
        title={t('company:GROUP_FORM_EDIT_TITLE')}
        width="360px"
        onClose={updateDialog.dialogProps.onClose}
        onSubmit={async (values) => {
          try {
            if (updateDialog.dialogRelevantValue)
              await companyStore.updateGroupData(updateDialog.dialogRelevantValue, values);
            updateDialog.dialogProps.onClose?.();
          } catch (error) {
            console.error(error);
          }
        }}
      >
        <FormTextField placeholder={t('tableTitle:NAME')} name="name" />
      </FormDialog>
      <ConfirmDeleteDialog
        type="groups"
        open={dialog.dialogProps.open}
        onClose={dialog.dialogProps.onClose}
        onConfirm={() => {
          if (dialog.dialogRelevantValue) {
            companyStore.deleteGroup(dialog?.dialogRelevantValue);
            companyStore.fetchGroups();
            if (dialog.dialogRelevantValue === selectedGroup) {
              setSelectedGroup(groups.at(0)?.id);
            }
          } else {
            console.error('No groups id provided');
          }
        }}
      />
    </>
  );
});
